import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  GET_CURRENCIES_URL,
  getAllBookingPropertiesURL,
  getAllReservationsURL,
  GetBookingInvoiceDetailsURL,
  getBookingPropertyURL,
  checkInReservationURL,
  togglePropertyListingURL,
  updateBookingStatusURL
} from "../../../utils/urls";
import Layout from "../component/layout";
import BlockDatesComponent from './blocking_modal';
import BookingModal from './booking_modal';
import BookingInvoiceDialog from "./booking_invoices/BookingInvoiceDialog";
import RecordPaymentDialog from "./booking_invoices/RecordPaymentDialog";
import CheckoutModal from "./checkout_modal";
import EditBookingPropertyForm from './edit_booking_property';
import ListUnitForBookingDialog from './list_unit_for_booking_dialog';
import reservationTrendService from "./reservation-trend-service";
import ReservationDetailsModal from './reservation_details';

const UnitListing = () => {
  // State variables
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [facilityId, setFacilityId] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showListingDialog, setShowListingDialog] = useState(false);
  const [showBlockingDialog, setShowBlockingDialog] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnitForBooking, setSelectedUnitForBooking] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currencies, setCurrencies] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedBookingInvoiceId, setSelectedBookingInvoiceId] = useState(null);
  const [showBookingInvoiceDialog, setShowBookingInvoiceDialog] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState({ label: 'All Statuses', value: 'all' });
  const [dateRange, setDateRange] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: '',
    statusFilter: { label: 'All Statuses', value: 'all' },
    dateRange: null
  });
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRefundAmount, setCancelRefundAmount] = useState(0);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Initialize component
  useEffect(() => {
    const initialize = async () => {
      try {
        const facilityId = await getItem("selectedFacilityId");
        if (!facilityId) {
          toastify("No facility selected", "error");
          return;
        }
        setFacilityId(facilityId);

        // Fetch all required data
        await Promise.all([
          fetchCurrencies(facilityId),
          fetchAllUnitsAndProperties(facilityId),
          fetchReservations(facilityId)
        ]);
      } catch (error) {
        console.error("Error initializing apartment listing:", error);
        setError(error.message || "Failed to load apartments data");
        toastify("Failed to load apartments data", "error");
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    // Parse the query parameters
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');

    // Set the active tab based on the 'tab' parameter
    if (tabParam === 'reservations') {
      setActiveIndex(1);
    }
  }, [location]);

  // Fetch currencies
  const fetchCurrencies = async (facilityId) => {
    try {
      const response = await makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, "GET");

      if (response.success) {
        let currencyData = [];

        if (Array.isArray(response.data)) {
          currencyData = response.data;
        } else if (response.data?.currencies && Array.isArray(response.data.currencies)) {
          currencyData = response.data.currencies;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          currencyData = response.data.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          currencyData = [response.data];
        }

        const processedCurrencies = currencyData.map(curr => ({
          _id: curr._id,
          code: curr.code || curr.currencyShortCode || 'Unknown',
          name: curr.name || curr.currencyName || 'Unknown Currency'
        }));

        setCurrencies(processedCurrencies);

        if (processedCurrencies.length === 0) {
          setCurrencies([
            { _id: "5f8b12345678901234567890", code: "USD", name: "US Dollar" },
            { _id: "5f8b12345678901234567891", code: "EUR", name: "Euro" },
            { _id: "5f8b12345678901234567892", code: "GBP", name: "British Pound" }
          ]);
        }
      } else {
        throw new Error(response.error || "Failed to fetch currencies");
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toastify("Error fetching currencies. Using default currencies.", "warning");

      setCurrencies([
        { _id: "5f8b12345678901234567890", code: "USD", name: "US Dollar" },
        { _id: "5f8b12345678901234567891", code: "EUR", name: "Euro" },
        { _id: "5f8b12345678901234567892", code: "GBP", name: "British Pound" }
      ]);
    }
  };

  // Fetch all apartments and their booking properties in one call
  const fetchAllUnitsAndProperties = async (facilityId) => {
    try {
      setLoading(true);

      const response = await makeRequest2(
        `${getAllBookingPropertiesURL}/${facilityId}`,
        "GET"
      );

      if (response.success) {
        let unitsData = [];

        if (Array.isArray(response.data)) {
          unitsData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          unitsData = response.data.data;
        }

        console.log("Apartments and properties from API:", unitsData);

        // Transform the data for the component
        const transformedUnits = unitsData.map(item => ({
          // Flatten apartment properties to root level for easier access
          _id: item.unit.id,
          name: item.unit.name,
          unitType: item.unit.unitType,
          division: item.unit.division,
          floorUnitNo: item.unit.floorUnitNo,
          status: item.unit.status,
          homeOwnerId: item.unit.homeOwnerId,
          isListedForBooking: item.unit.isListedForBooking,
          // Keep booking property as nested object
          bookingProperty: item.bookingProperty
        }));

        setAllUnits(transformedUnits);
        console.log("Transformed apartments:", transformedUnits);

      } else {
        throw new Error(response.error || "Failed to fetch apartments and properties");
      }
    } catch (error) {
      console.error("Error fetching apartments and properties:", error);
      setError(error.message || "Failed to load apartments data");
      toastify("Failed to load apartments data", "error");
      setAllUnits([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reservations
  const fetchReservations = async (facilityId) => {
    try {
      const response = await makeRequest2(
        `${getAllReservationsURL}/${facilityId}`,
        "GET"
      );

      if (response.success) {
        let reservationsData = [];

        if (Array.isArray(response.data)) {
          reservationsData = response.data;
        } else if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data.data)) {
            reservationsData = response.data.data;
          } else if (response.data.reservations && Array.isArray(response.data.reservations)) {
            reservationsData = response.data.reservations;
          } else {
            reservationsData = [response.data];
          }
        }

        setReservations(reservationsData);
      } else {
        throw new Error(response.error || "Failed to fetch reservations");
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setError(error.message || "Failed to load reservations");
      toastify("Failed to load reservations", "error");
      setReservations([]);
    }
  };

  // Handle successful listing creation
  const handleListingSuccess = async (createdProperty) => {
    toastify("Apartment successfully listed for booking", "success");
    await fetchAllUnitsAndProperties(facilityId);
  };

  // Handle successful blocking
  const handleBlockingSuccess = async () => {
    await fetchAllUnitsAndProperties(facilityId);
  };

  const openBookingModal = (unit) => {
    const formattedUnit = {
      unit: {
        id: unit._id || unit.id,
        name: unit.name,
        unitType: unit.unitType || 'Unknown',
        ...unit
      },
      bookingProperty: {
        id: unit.bookingProperty?.id || unit.bookingProperty?._id,
        propertyName: unit.bookingProperty?.propertyName || unit.name,
        basePrice: unit.bookingProperty?.basePrice || 0,
        currencyId: unit.bookingProperty?.currencyId,
        managedByLandlord: unit.bookingProperty?.managedByLandlord || false,
        ...unit.bookingProperty
      }
    };

    setSelectedUnitForBooking(formattedUnit);
    setShowBookingModal(true);
  };

  const openDetailsModal = (reservation) => {
    setSelectedReservationId(reservation._id || reservation.bookingReservationId);
    setShowDetailsModal(true);
  };

  // Open edit dialog
  const openEditDialog = async (unit) => {
    if (!unit.bookingProperty) {
      toastify("This apartment is not listed for booking yet", "error");
      return;
    }

    try {
      setLoading(true);

      const response = await makeRequest2(
        `${getBookingPropertyURL}/${facilityId}/${unit.bookingProperty.id}`,
        "GET"
      );

      if (response.success) {
        const bookingPropertyData = response.data?.data || response.data;

        setSelectedUnit({
          ...unit,
          bookingProperty: bookingPropertyData
        });
        setShowEditDialog(true);
      } else {
        throw new Error(response.error || "Failed to fetch property details");
      }
    } catch (error) {
      console.error("Error fetching property details:", error);
      toastify("Failed to load property details for editing", "error");
    } finally {
      setLoading(false);
    }
  };

  // Open checkout modal
  const openCheckoutModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowCheckoutModal(true);
  };

  const openRecordPaymentDialog = async (reservation) => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }

      const invoiceLookupId = reservation.invoiceId || reservation.invoiceNumber;
      if (!invoiceLookupId) {
        toastify("No invoice linked to this reservation", "error");
        return;
      }

      const response = await makeRequest2(
        `${GetBookingInvoiceDetailsURL}/${facilityId}/${invoiceLookupId}`,
        "GET"
      );

      if (response.success) {
        const invoiceData = response.data?.data || response.data;
        setSelectedInvoice(invoiceData);
        setShowRecordPaymentDialog(true);
      } else {
        toastify(response.error || "Failed to load invoice details", "error");
      }
    } catch (error) {
      console.error("Error loading invoice details:", error);
      toastify("Failed to load invoice details", "error");
    }
  };

  // Check in a guest whose reservation has payment applied (status 'reserved')
  const handleCheckIn = async (reservation) => {
    try {
      const fid = await getItem("selectedFacilityId");
      if (!fid) {
        toastify("No facility selected", "error");
        return;
      }

      const response = await makeRequest2(
        `${checkInReservationURL}/${fid}/${reservation._id}`,
        "POST",
        {}
      );

      if (response.success) {
        toastify("Guest checked in successfully", "success");
        await fetchReservations(fid);
      } else {
        toastify(response.error || "Failed to check in guest", "error");
      }
    } catch (error) {
      console.error("Error checking in guest:", error);
      toastify("Failed to check in guest", "error");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const normalizeReservationStatus = (status) => {
    const statusMap = {
      canceled: 'cancelled',
      cancelled: 'cancelled',
      booked: 'booked',
      reserved: 'reserved',
      'checked-in': 'checked-in',
      'checked-out': 'checked-out',
      completed: 'completed'
    };

    return statusMap[status] || status;
  };

  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value._id || value.id || value.toString?.();
    }
    return value.toString();
  };

  const getDateRange = () => {
    if (appliedFilters.dateRange && appliedFilters.dateRange[0] && appliedFilters.dateRange[1]) {
      const start = new Date(appliedFilters.dateRange[0]);
      const end = new Date(appliedFilters.dateRange[1]);
      const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
      const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999);
      return { startDate, endDate };
    }

    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return { startDate, endDate };
  };

  const normalizeDateOnly = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  };

  const isRangeOverlapping = (startA, endA, startB, endB) => {
    if (!startA || !endA || !startB || !endB) return false;
    return startA < endB && endA > startB;
  };

  const getUnitBookingStatus = (unit) => {
    if (!unit.bookingProperty || !unit.bookingProperty.isListed) {
      return 'not_listed';
    }

    const { startDate, endDate } = getDateRange();
    const unitId = normalizeId(unit._id || unit.id);
    const searchStart = normalizeDateOnly(startDate);
    const searchEnd = normalizeDateOnly(endDate);

    const overlapping = reservations.filter((reservation) => {
      const reservationUnitId = normalizeId(reservation.unitId);
      if (!reservationUnitId || reservationUnitId !== unitId) return false;

      const normalizedStatus = normalizeReservationStatus(reservation.status);
      if (!['reserved', 'booked', 'checked-in', 'checked-out', 'completed'].includes(normalizedStatus)) return false;

      const checkIn = normalizeDateOnly(reservation.checkIn);
      const checkOut = normalizeDateOnly(reservation.checkOut);
      return isRangeOverlapping(checkIn, checkOut, searchStart, searchEnd);
    });

    const blockedDates = Array.isArray(unit.bookingProperty?.blockedDates)
      ? unit.bookingProperty.blockedDates
      : [];
    const hasBlockedOverlap = blockedDates.some((block) => {
      const blockStart = normalizeDateOnly(block.startDate || block.from || block.start);
      const blockEnd = normalizeDateOnly(block.endDate || block.to || block.end);
      return isRangeOverlapping(blockStart, blockEnd, searchStart, searchEnd);
    });

    if (hasBlockedOverlap) {
      return 'reserved';
    }

    if (overlapping.some((reservation) => normalizeReservationStatus(reservation.status) === 'booked')) {
      return 'booked';
    }

    if (overlapping.some((reservation) => normalizeReservationStatus(reservation.status) === 'reserved')) {
      return 'reserved';
    }

    if (overlapping.some((reservation) => normalizeReservationStatus(reservation.status) === 'completed')) {
      return 'completed';
    }

    return 'available';
  };

  const occupancyStatusTemplate = (rowData) => {
    const status = getUnitBookingStatus(rowData);
    const statusStyles = {
      available: { className: 'bg-success', label: 'Available' },
      reserved: { className: 'bg-warning', label: 'Reserved' },
      booked: { className: 'bg-primary', label: 'Booked' },
      completed: { className: 'bg-info', label: 'Completed' },
      not_listed: { className: 'bg-secondary', label: 'Not Listed' }
    };

    const style = statusStyles[status] || statusStyles.not_listed;
    return (
      <span className={`status-badge ${style.className}`}>
        {style.label}
      </span>
    );
  };

  const statusFilterOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Available', value: 'available' },
    { label: 'Reserved', value: 'reserved' },
    { label: 'Booked', value: 'booked' },
    { label: 'Completed', value: 'completed' }
  ];

  const filteredUnits = allUnits.filter((unit) => {
    const matchesSearch = `${unit.name || ''} ${unit.unitType || ''} ${unit.division || ''} ${unit.floorUnitNo || ''}`
      .toLowerCase()
      .includes(appliedFilters.searchTerm.trim().toLowerCase());

    if (!matchesSearch) {
      return false;
    }

    const hasDateRange = appliedFilters.dateRange && appliedFilters.dateRange[0] && appliedFilters.dateRange[1];
    if (appliedFilters.statusFilter?.value && appliedFilters.statusFilter.value !== 'all') {
      return getUnitBookingStatus(unit) === appliedFilters.statusFilter.value;
    }

    if (hasDateRange) {
      return getUnitBookingStatus(unit) === 'available';
    }

    return true;
  });

  const bookedUnitsReservations = reservations.filter((reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation.status);
    return normalizedStatus === 'booked' || normalizedStatus === 'reserved';
  });
  const checkedInReservations = reservations.filter((reservation) => normalizeReservationStatus(reservation.status) === 'checked-in');
  const canceledReservations = reservations.filter((reservation) => normalizeReservationStatus(reservation.status) === 'cancelled');
  const completedReservations = reservations.filter((reservation) => {
    const normalizedStatus = normalizeReservationStatus(reservation.status);
    return normalizedStatus === 'completed' || normalizedStatus === 'checked-out';
  });

  // Handle successful checkout
  const handleCheckoutSuccess = async (checkoutData) => {
    toastify("Checkout completed successfully", "success");

    try {
      await Promise.all([
        fetchAllUnitsAndProperties(facilityId),
        fetchReservations(facilityId)
      ]);
    } catch (error) {
      console.error("Error updating data after checkout:", error);
    }
  };

  // Handle successful booking creation
  const handleBookingSuccess = async (bookingData) => {
    toastify("Booking created successfully", "success");

    try {
      await fetchAllUnitsAndProperties(facilityId);
      const normalizedStatus = normalizeReservationStatus(bookingData?.status || 'reserved');
      await reservationTrendService.updateTrendData(new Date(), normalizedStatus);
      await fetchReservations(facilityId);
    } catch (error) {
      console.error("Error updating data after booking:", error);
    }

    const bookingIdentifier = bookingData?.invoiceId || bookingData?._id;
    if (bookingIdentifier) {
      setSelectedBookingInvoiceId(bookingIdentifier);
      setShowBookingInvoiceDialog(true);
    }
  };

  // Open listing dialog for specific apartment
  const openListingDialog = (unit) => {
    setSelectedUnit(unit);
    setShowListingDialog(true);
  };

  // Open blocking dialog
  const openBlockingDialog = (unit) => {
    if (!unit.bookingProperty) {
      toastify("This apartment is not listed for booking yet", "error");
      return;
    }

    setSelectedUnit(unit);
    setShowBlockingDialog(true);
  };

  const openCancelDialog = (reservation) => {
    setReservationToCancel(reservation);
    setCancelReason('');
    setCancelRefundAmount(0);
    setShowCancelDialog(true);
  };

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
    setReservationToCancel(null);
  };

  const getReservationCurrencyCode = (reservation) => {
    if (!reservation) return 'N/A';
    if (reservation.currencyId && typeof reservation.currencyId === 'object') {
      return reservation.currencyId.currencyShortCode || reservation.currencyId.symbol || reservation.currencyId.code || 'N/A';
    }
    if (reservation.currencyId) {
      const currencyObj = currencies.find(c =>
        c._id?.toString() === reservation.currencyId?.toString()
      );
      if (currencyObj) {
        return currencyObj.code || currencyObj.currencyShortCode || currencyObj.symbol || 'N/A';
      }
    }
    return 'N/A';
  };

  const handleCancelReservation = async () => {
    if (!reservationToCancel) return;
    if (!cancelReason.trim()) {
      toastify("Cancellation reason is required", "error");
      return;
    }

    try {
      setCancelSubmitting(true);
      const reservationId = reservationToCancel._id || reservationToCancel.bookingReservationId;
      const response = await makeRequest2(
        `${updateBookingStatusURL}/${facilityId}/${reservationId}`,
        "PATCH",
        {
          status: "cancelled",
          cancellationDetails: {
            reason: cancelReason.trim(),
            refundAmount: cancelRefundAmount || 0
          }
        }
      );

      if (response.success) {
        toastify("Reservation cancelled successfully", "success");
        reservationTrendService.updateTrendData(new Date(), 'cancelled');
        await Promise.all([
          fetchAllUnitsAndProperties(facilityId),
          fetchReservations(facilityId)
        ]);
        closeCancelDialog();
      } else {
        throw new Error(response.error || "Failed to cancel reservation");
      }
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      toastify(error.message || "Failed to cancel reservation", "error");
    } finally {
      setCancelSubmitting(false);
    }
  };

  // Toggle apartment listing status
  const toggleListing = async (unit) => {
    try {
      if (!unit.bookingProperty) {
        toastify("This apartment is not listed for booking yet", "error");
        return;
      }

      setLoading(true);
      setError(null);

      const newListingStatus = !unit.bookingProperty.isListed;

      const response = await makeRequest2(
        `${togglePropertyListingURL}/${facilityId}/${unit.bookingProperty.id}`,
        "PATCH",
        { isListed: newListingStatus }
      );

      if (response.success) {
        toastify(`Unit ${unit.bookingProperty.isListed ? 'delisted' : 'listed'} successfully`, "success");

        await fetchAllUnitsAndProperties(facilityId);
      } else {
        throw new Error(response.error || "Failed to toggle apartment listing status");
      }
    } catch (error) {
      console.error("Error toggling apartment listing status:", error);
      setError(error.message || "Failed to update apartment listing status");
      toastify("Failed to update apartment listing status", "error");
    } finally {
      setLoading(false);
    }
  };

  // DataTable templates
  const unitNameTemplate = (rowData) => {
    return <span className="font-medium text-gray-800">{rowData.name}</span>;
  };

  const unitTypeTemplate = (rowData) => {
    return <span className="text-gray-600">{rowData.unitType || 'N/A'}</span>;
  };

  const unitDivisionTemplate = (rowData) => {
    return <span className="text-gray-600">{rowData.division || 'N/A'}</span>;
  };

  const bookingStatusTemplate = (rowData) => {
    if (!rowData.bookingProperty) {
      return <span className="text-gray-500">Not Listed</span>;
    }

    const isListed = rowData.bookingProperty?.isListed;
    const managedByLandlord = rowData.bookingProperty?.managedByLandlord;

    return (
      <div className="d-flex flex-column">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isListed ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
          {isListed ? 'Listed' : 'Not Listed'}
        </span>
        {rowData.bookingProperty && (
          <small className="text-muted mt-1">
            {managedByLandlord ? 'Landlord Managed' : 'Property Manager'}
          </small>
        )}
      </div>
    );
  };

  const unitPriceTemplate = (rowData) => {
    if (!rowData.bookingProperty) {
      return <span className="text-gray-400">-</span>;
    }

    if (rowData.bookingProperty.managedByLandlord) {
      return <span className="text-gray-400">Landlord Managed</span>;
    }

    // Use the populated currency object from the backend
    let currencySymbol = 'N/A';

    if (rowData.bookingProperty.currency && rowData.bookingProperty.currency.code) {
      // Backend provides populated currency object
      currencySymbol = rowData.bookingProperty.currency.code;
    } else if (rowData.bookingProperty.currencyId) {
      // Fallback: try to find in currencies array if population failed
      const currencyObj = currencies.find(c =>
        c._id?.toString() === rowData.bookingProperty.currencyId?.toString()
      );
      if (currencyObj && currencyObj.code) {
        currencySymbol = currencyObj.code;
      }
    }

    return <span className="text-gray-800 font-medium">{currencySymbol} {rowData.bookingProperty?.basePrice || '0.00'}</span>;
  };

  const actionTemplate = (rowData) => {
    const hasBookingProperty = !!rowData.bookingProperty;
    const isListed = rowData.bookingProperty?.isListed;

    return (
      <div className="flex flex-row justify-center gap-2">
        {/* List for booking button - only for apartments not yet listed */}
        {!hasBookingProperty && (
          <Button
            icon="ti ti-building-store"
            className="p-button-rounded p-button-info"
            onClick={() => openListingDialog(rowData)}
            tooltip="List for booking"
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {/* Book Now button - only for listed apartments */}
        {hasBookingProperty && isListed && (
          <Button
            icon="ti ti-calendar-plus"
            className="p-button-rounded p-button-success"
            onClick={() => openBookingModal(rowData)}
            tooltip="Book Now"
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {/* Block Dates button - only for listed apartments */}
        {hasBookingProperty && isListed && (
          <Button
            icon="ti ti-calendar-off"
            className="p-button-rounded p-button-secondary"
            onClick={() => openBlockingDialog(rowData)}
            tooltip="Block Dates"
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {/* Toggle listing button - only for apartments with booking properties */}
        {hasBookingProperty && (
          <Button
            icon={`ti ti-${isListed ? 'eye-off' : 'eye'}`}
            className={`p-button-rounded ${isListed ? 'p-button-warning' : 'p-button-primary'}`}
            onClick={() => toggleListing(rowData)}
            tooltip={isListed ? 'Delist' : 'List'}
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {/* Edit property button - only for apartments with booking properties */}
        {hasBookingProperty && (
          <Button
            icon="ti ti-edit"
            className="p-button-rounded p-button-primary"
            onClick={() => openEditDialog(rowData)}
            tooltip="Edit Property"
            tooltipOptions={{ position: 'top' }}
          />
        )}
      </div>
    );
  };

  // Empty template for DataTable
  const emptyTemplate = () => {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <i className="ti ti-building text-gray-300 text-4xl mb-3"></i>
        <h6 className="text-base font-semibold text-gray-700 mb-1">No apartments Found</h6>
        <p className="text-gray-500 text-sm mb-4">There are no eligible apartments available for booking.</p>
      </div>
    );
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/booking-management">Booking Management</Link>
                </li>
                <li className="breadcrumb-item active">Apartment Listing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
            <div className="card">
            <div className="card-header">
              <h5>Apartment Listing Management</h5>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-lg-4 col-md-6">
                  <label className="form-label text-muted small">Search Apartments</label>
                  <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, type, division..."
                    className="w-100"
                  />
                </div>
                <div className="col-lg-3 col-md-6">
                  <label className="form-label text-muted small">Booking Status</label>
                  <Dropdown
                    value={statusFilter}
                    options={statusFilterOptions}
                    onChange={(e) => setStatusFilter(e.value)}
                    optionLabel="label"
                    className="w-100"
                  />
                </div>
                <div className="col-lg-5 col-md-12">
                  <label className="form-label text-muted small">Search Dates</label>
                  <div className="d-flex flex-column flex-md-row gap-2">
                    <Calendar
                      value={dateRange}
                      onChange={(e) => setDateRange(e.value)}
                      selectionMode="range"
                      readOnlyInput
                      placeholder="Select date range"
                      className="w-100"
                      showIcon
                    />
                    <Button
                      label="Search"
                      icon="ti ti-search"
                      className="p-button-sm p-button-primary"
                      onClick={() => {
                        setAppliedFilters({
                          searchTerm,
                          statusFilter,
                          dateRange
                        });
                      }}
                    />
                    {dateRange && dateRange[0] && dateRange[1] && (
                      <div className="d-flex align-items-center text-muted small">
                        {Math.max(
                          1,
                          Math.ceil((new Date(dateRange[1]) - new Date(dateRange[0])) / (1000 * 60 * 60 * 24))
                        )}{" "}
                        nights
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger mb-4">
                  <p className="mb-0">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="d-flex justify-content-center align-items-center flex-column py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="mb-3">Loading Apartments Data</h6>
                  <p className="text-muted">Please wait while we fetch your apartments information...</p>
                </div>
              ) : (
                <>
                  <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    {/* All apartments Tab */}
                    <TabPanel header="All Apartments">
                      <div className="table-responsive">
                        <DataTable
                          value={filteredUnits}
                          emptyMessage={emptyTemplate}
                          paginator
                          rows={10}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          stripedRows
                          sortField="name"
                          sortOrder={1}
                        >
                          <Column field="name" header="Apartment Name" body={unitNameTemplate} sortable />
                          <Column field="unitType" header="Type" body={unitTypeTemplate} sortable />
                          <Column field="division" header="Division" body={unitDivisionTemplate} sortable />
                          <Column field="bookingProperty" header="Booking Status" body={bookingStatusTemplate} sortable />
                          <Column field="bookingProperty.basePrice" header="Price" body={unitPriceTemplate} sortable />
                          <Column header="Actions" body={actionTemplate} style={{ width: '250px', textAlign: 'center' }} />
                        </DataTable>
                      </div>
                    </TabPanel>

                    {/* Booked Units Tab - merges booked (unpaid) and reserved (paid) reservations */}
                    <TabPanel header="Booked Units">
                      <div className="table-responsive">
                        <DataTable
                          value={bookedUnitsReservations}
                          emptyMessage="No booked units found. Create bookings to see them here."
                          paginator
                          rows={10}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          stripedRows
                          sortField="checkIn"
                          sortOrder={-1}
                        >
                          <Column
                            field="bookingReservationId"
                            header="Booking ID"
                            sortable
                            body={(rowData) => (
                              <Button
                                label={rowData.bookingReservationId || "N/A"}
                                link
                                className="p-0 text-primary"
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                  setSelectedBookingInvoiceId(rowData._id);
                                  setShowBookingInvoiceDialog(true);
                                }}
                              />
                            )}
                          />
                          <Column
                            field="guestInfo.name"
                            header="Guest"
                            body={(rowData) => (
                              <div className="guest-info">
                                <span className="guest-avatar">{(rowData.guestInfo?.name || 'Guest').charAt(0)}</span>
                                <span className="guest-name">{rowData.guestInfo?.name || 'Unknown Guest'}</span>
                              </div>
                            )}
                            sortable
                          />

                          <Column
                            field="unitId"
                            header="Apartment"
                            body={(rowData) => {
                              if (rowData.unitId && typeof rowData.unitId === 'object' && rowData.unitId.name) {
                                return rowData.unitId.name;
                              }

                              const unitIdStr = typeof rowData.unitId === 'string' ? rowData.unitId :
                                (rowData.unitId?._id || rowData.unitId?.toString?.());

                              const unit = allUnits.find(u => {
                                const unitId = u._id?.toString ? u._id.toString() : u._id;
                                return unitId === unitIdStr;
                              });
                              return unit ? unit.name : 'Unknown Apartment';
                            }}
                            sortable
                          />
                          <Column
                            field="checkIn"
                            header="Check-in"
                            body={(rowData) => formatDate(rowData.checkIn)}
                            sortable
                          />
                          <Column
                            field="checkOut"
                            header="Check-out"
                            body={(rowData) => formatDate(rowData.checkOut)}
                            sortable
                          />
                          <Column
                            field="totalAmount"
                            header="Amount"
                            body={(rowData) => {
                              if (!rowData.totalAmount && !rowData.basePrice) {
                                return <span className="text-gray-400">Landlord Managed</span>;
                              }

                              let currencySymbol = 'N/A';

                              if (rowData.currencyId && typeof rowData.currencyId === 'object') {
                                currencySymbol = rowData.currencyId.currencyShortCode || rowData.currencyId.symbol || 'N/A';
                              } else if (rowData.currencyId) {
                                const currencyObj = currencies.find(c =>
                                  c._id?.toString() === rowData.currencyId?.toString()
                                );
                                if (currencyObj && currencyObj.code) {
                                  currencySymbol = currencyObj.code;
                                }
                              }

                              const amount = rowData.totalAmount || rowData.basePrice || 0;
                              return `${currencySymbol} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            }}
                            sortable
                          />
                          <Column
                            field="paymentTiming"
                            header="Payment Timing"
                            body={(rowData) => rowData.paymentTiming || 'N/A'}
                            sortable
                          />
                          <Column
                            field="totalAmount"
                            header="Amount"
                            body={(rowData) => {
                              if (!rowData.totalAmount && !rowData.basePrice) {
                                return <span className="text-gray-400">Landlord Managed</span>;
                              }

                              // Use populated currency from backend
                              let currencySymbol = 'N/A';

                              if (rowData.currencyId && typeof rowData.currencyId === 'object') {
                                // Currency is populated by backend
                                currencySymbol = rowData.currencyId.currencyShortCode || rowData.currencyId.symbol || 'N/A';
                              } else if (rowData.currencyId) {
                                // Fallback: currency not populated, search in currencies array
                                const currencyObj = currencies.find(c =>
                                  c._id?.toString() === rowData.currencyId?.toString()
                                );
                                if (currencyObj && currencyObj.code) {
                                  currencySymbol = currencyObj.code;
                                }
                              }

                              const amount = rowData.totalAmount || rowData.basePrice || 0;
                              return `${currencySymbol} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            }}
                            sortable
                          />
                          <Column
                            field="paymentTiming"
                            header="Payment Timing"
                            body={(rowData) => rowData.paymentTiming || 'N/A'}
                            sortable
                          />
                          <Column
                            field="status"
                            header="Status"
                            body={(rowData) => {
                              const statusMap = {
                                // New status values
                                'reserved': { class: 'bg-warning', text: 'Reserved' },
                                'booked': { class: 'bg-success', text: 'Booked' },
                                'canceled': { class: 'bg-danger', text: 'Canceled' },
                                'completed': { class: 'bg-info', text: 'Completed' },
                                // Legacy status values (for backward compatibility)
                                'new': { class: 'bg-warning', text: 'Reserved' },
                                'confirmed': { class: 'bg-success', text: 'Booked' },
                                'cancelled': { class: 'bg-danger', text: 'Canceled' }
                              };

                              const style = rowData.checkoutPending
                                ? { class: 'bg-warning', text: 'Pending' }
                                : statusMap[rowData.status] || { class: 'bg-secondary', text: rowData.status };

                              return (
                                <span className={`status-badge ${style.class}`}>
                                  {style.text}
                                </span>
                              );
                            }}
                            sortable
                          />
                          <Column
                            header="Actions"
                            body={(rowData) => {
                              // reserved: new, not yet confirmed. booked: confirmed -
                              // mpesa_payment.js only moves reserved -> booked once
                              // the invoice is FULLY paid. checked-in: guest on
                              // premises. checked-out: guest left, balance
                              // outstanding. (Unlike Swan's fork, "booked" here does
                              // NOT mean zero-payment.)
                              const normalizedStatus = normalizeReservationStatus(rowData.status);
                              const isBooked = normalizedStatus === 'booked';
                              const isReserved = normalizedStatus === 'reserved';
                              const isCheckedIn = normalizedStatus === 'checked-in';
                              const isCheckedOut = normalizedStatus === 'checked-out';
                              const canCancel = isBooked || isReserved;
                              const canRecordPayment = isBooked || isReserved || isCheckedOut;

                              // Check-in eligibility depends on the reservation's
                              // payment timing rule, not just its status - a
                              // partial payment alone shouldn't unlock check-in
                              // unless the timing rule allows it. 'booked' is the
                              // normal payment-confirmed gate; 'reserved' is also
                              // accepted for After/PerNight, which never require an
                              // upfront payment to reach 'booked' in the first place.
                              const totalAmount = Number(rowData.totalAmount || 0);
                              const totalPaid = Number(rowData.totalPaidAmount || 0);
                              const paidFraction = totalAmount > 0 ? (totalPaid / totalAmount) : 0;
                              const timing = rowData.paymentTiming || 'Before';
                              let canCheckIn = false;
                              if (isBooked) {
                                if (timing === 'Split') {
                                  const requiredFraction = (Number(rowData.splitPaymentPercentage) || 50) / 100;
                                  canCheckIn = paidFraction >= requiredFraction;
                                } else if (timing === 'After' || timing === 'PerNight') {
                                  canCheckIn = true;
                                } else {
                                  // 'Before' - full payment required
                                  canCheckIn = paidFraction >= 1;
                                }
                              } else if (isReserved && (timing === 'After' || timing === 'PerNight')) {
                                // No payment gate for these timings - guest can
                                // check in immediately and pay on/after checkout
                                // or per night.
                                canCheckIn = true;
                              }

                              return (
                                <div className="d-flex gap-2">
                                  <Button
                                    icon="ti ti-eye"
                                    className="p-button-rounded p-button-info p-button-sm"
                                    onClick={() => openDetailsModal(rowData)}
                                    tooltip="View Details"
                                    tooltipOptions={{ position: 'top' }}
                                  />
                                  {canCheckIn && (
                                    <Button
                                      icon="ti ti-login"
                                      className="p-button-rounded p-button-success p-button-sm"
                                      onClick={() => handleCheckIn(rowData)}
                                      tooltip="Check In"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                  {(isReserved || isBooked) && !canCheckIn && (
                                    <Button
                                      icon="ti ti-lock"
                                      className="p-button-rounded p-button-secondary p-button-sm"
                                      disabled
                                      tooltip={
                                        timing === 'Split'
                                          ? `Check-in unlocks at ${(Number(rowData.splitPaymentPercentage) || 50)}% paid`
                                          : "Full payment required before check-in"
                                      }
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                  {isCheckedIn && (
                                    <Button
                                      icon="ti ti-logout"
                                      className="p-button-rounded p-button-success p-button-sm"
                                      onClick={() => openCheckoutModal(rowData)}
                                      tooltip="Checkout"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                  {canRecordPayment && (
                                    <Button
                                      icon="ti ti-credit-card"
                                      className="p-button-rounded p-button-warning p-button-sm"
                                      onClick={() => openRecordPaymentDialog(rowData)}
                                      tooltip="Record Payment"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                  {canCancel && (
                                    <Button
                                      icon="ti ti-x"
                                      className="p-button-rounded p-button-danger p-button-sm"
                                      onClick={() => openCancelDialog(rowData)}
                                      tooltip="Cancel Reservation"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                </div>
                              );
                            }}
                            style={{ width: '160px', textAlign: 'center' }}
                          />
                        </DataTable>
                      </div>
                    </TabPanel>

                    {/* Checked-in Tab - guests currently on premises */}
                    <TabPanel header="Checked-in">
                      <div className="table-responsive">
                        <DataTable
                          value={checkedInReservations}
                          emptyMessage="No checked-in guests found."
                          paginator
                          rows={10}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          stripedRows
                          sortField="checkedInAt"
                          sortOrder={-1}
                        >
                          <Column
                            field="bookingReservationId"
                            header="Booking ID"
                            sortable
                            body={(rowData) => (
                              <Button
                                label={rowData.bookingReservationId || "N/A"}
                                link
                                className="p-0 text-primary"
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                  setSelectedBookingInvoiceId(rowData._id);
                                  setShowBookingInvoiceDialog(true);
                                }}
                              />
                            )}
                          />
                          <Column
                            field="guestInfo.name"
                            header="Guest"
                            body={(rowData) => (
                              <div className="guest-info">
                                <span className="guest-avatar">{(rowData.guestInfo?.name || 'Guest').charAt(0)}</span>
                                <span className="guest-name">{rowData.guestInfo?.name || 'Unknown Guest'}</span>
                              </div>
                            )}
                            sortable
                          />
                          <Column
                            field="unitId"
                            header="Apartment"
                            body={(rowData) => {
                              if (rowData.unitId && typeof rowData.unitId === 'object' && rowData.unitId.name) {
                                return rowData.unitId.name;
                              }

                              const unitIdStr = typeof rowData.unitId === 'string' ? rowData.unitId :
                                (rowData.unitId?._id || rowData.unitId?.toString?.());

                              const unit = allUnits.find(u => {
                                const unitId = u._id?.toString ? u._id.toString() : u._id;
                                return unitId === unitIdStr;
                              });
                              return unit ? unit.name : 'Unknown Apartment';
                            }}
                            sortable
                          />
                          <Column
                            field="checkedInAt"
                            header="Checked In"
                            body={(rowData) => rowData.checkedInAt ? formatDate(rowData.checkedInAt) : 'N/A'}
                            sortable
                          />
                          <Column
                            field="checkOut"
                            header="Expected Check-out"
                            body={(rowData) => formatDate(rowData.checkOut)}
                            sortable
                          />
                          <Column
                            header="Actions"
                            body={(rowData) => (
                              <div className="d-flex gap-2">
                                <Button
                                  icon="ti ti-eye"
                                  className="p-button-rounded p-button-info p-button-sm"
                                  onClick={() => openDetailsModal(rowData)}
                                  tooltip="View Details"
                                  tooltipOptions={{ position: 'top' }}
                                />
                                <Button
                                  icon="ti ti-logout"
                                  className="p-button-rounded p-button-success p-button-sm"
                                  onClick={() => openCheckoutModal(rowData)}
                                  tooltip="Checkout"
                                  tooltipOptions={{ position: 'top' }}
                                />
                                <Button
                                  icon="ti ti-credit-card"
                                  className="p-button-rounded p-button-warning p-button-sm"
                                  onClick={() => openRecordPaymentDialog(rowData)}
                                  tooltip="Record Payment"
                                  tooltipOptions={{ position: 'top' }}
                                />
                              </div>
                            )}
                            style={{ width: '160px', textAlign: 'center' }}
                          />
                        </DataTable>
                      </div>
                    </TabPanel>

                    {/* Cancelled apartments Tab */}
                    <TabPanel header="Cancelled Apartments">
                      <div className="table-responsive">
                        <DataTable
                          value={canceledReservations}
                          emptyMessage="No cancelled reservations found."
                          paginator
                          rows={10}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          stripedRows
                          sortField="checkIn"
                          sortOrder={-1}
                        >
                          <Column
                            field="bookingReservationId"
                            header="Booking ID"
                            sortable
                            body={(rowData) => (
                              <Button
                                label={rowData.bookingReservationId || "N/A"}
                                link
                                className="p-0 text-primary"
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                  setSelectedBookingInvoiceId(rowData._id);
                                  setShowBookingInvoiceDialog(true);
                                }}
                              />
                            )}
                          />
                          <Column
                            field="guestInfo.name"
                            header="Guest"
                            body={(rowData) => (
                              <div className="guest-info">
                                <span className="guest-avatar">{(rowData.guestInfo?.name || 'Guest').charAt(0)}</span>
                                <span className="guest-name">{rowData.guestInfo?.name || 'Unknown Guest'}</span>
                              </div>
                            )}
                            sortable
                          />
                          <Column
                            field="unitId"
                            header="Apartment"
                            body={(rowData) => {
                              if (rowData.unitId && typeof rowData.unitId === 'object' && rowData.unitId.name) {
                                return rowData.unitId.name;
                              }

                              const unitIdStr = typeof rowData.unitId === 'string' ? rowData.unitId :
                                (rowData.unitId?._id || rowData.unitId?.toString?.());

                              const unit = allUnits.find(u => {
                                const unitId = u._id?.toString ? u._id.toString() : u._id;
                                return unitId === unitIdStr;
                              });
                              return unit ? unit.name : 'Unknown Apartment';
                            }}
                            sortable
                          />
                          <Column
                            field="checkIn"
                            header="Check-in"
                            body={(rowData) => formatDate(rowData.checkIn)}
                            sortable
                          />
                          <Column
                            field="checkOut"
                            header="Check-out"
                            body={(rowData) => formatDate(rowData.checkOut)}
                            sortable
                          />
                          <Column
                            field="status"
                            header="Status"
                            body={(rowData) => (
                              <span className="status-badge bg-danger">Canceled</span>
                            )}
                            sortable
                          />
                          <Column
                            header="Actions"
                            body={(rowData) => (
                              <div className="d-flex gap-2">
                                <Button
                                  icon="ti ti-eye"
                                  className="p-button-rounded p-button-info p-button-sm"
                                  onClick={() => openDetailsModal(rowData)}
                                  tooltip="View Details"
                                  tooltipOptions={{ position: 'top' }}
                                />
                              </div>
                            )}
                            style={{ width: '120px', textAlign: 'center' }}
                          />
                        </DataTable>
                      </div>
                    </TabPanel>

                    {/* Completed apartments Tab */}
                    <TabPanel header="Completed Apartments">
                      <div className="table-responsive">
                        <DataTable
                          value={completedReservations}
                          emptyMessage="No completed reservations found."
                          paginator
                          rows={10}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          responsiveLayout="scroll"
                          className="p-datatable-sm"
                          stripedRows
                          sortField="checkOut"
                          sortOrder={-1}
                        >
                          <Column
                            field="bookingReservationId"
                            header="Booking ID"
                            sortable
                            body={(rowData) => (
                              <Button
                                label={rowData.bookingReservationId || "N/A"}
                                link
                                className="p-0 text-primary"
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                  setSelectedBookingInvoiceId(rowData._id);
                                  setShowBookingInvoiceDialog(true);
                                }}
                              />
                            )}
                          />
                          <Column
                            field="guestInfo.name"
                            header="Guest"
                            body={(rowData) => (
                              <div className="guest-info">
                                <span className="guest-avatar">{(rowData.guestInfo?.name || 'Guest').charAt(0)}</span>
                                <span className="guest-name">{rowData.guestInfo?.name || 'Unknown Guest'}</span>
                              </div>
                            )}
                            sortable
                          />
                          <Column
                            field="unitId"
                            header="Apartment"
                            body={(rowData) => {
                              if (rowData.unitId && typeof rowData.unitId === 'object' && rowData.unitId.name) {
                                return rowData.unitId.name;
                              }

                              const unitIdStr = typeof rowData.unitId === 'string' ? rowData.unitId :
                                (rowData.unitId?._id || rowData.unitId?.toString?.());

                              const unit = allUnits.find(u => {
                                const unitId = u._id?.toString ? u._id.toString() : u._id;
                                return unitId === unitIdStr;
                              });
                              return unit ? unit.name : 'Unknown Apartment';
                            }}
                            sortable
                          />
                          <Column
                            field="checkIn"
                            header="Check-in"
                            body={(rowData) => formatDate(rowData.checkIn)}
                            sortable
                          />
                          <Column
                            field="checkOut"
                            header="Check-out"
                            body={(rowData) => formatDate(rowData.checkOut)}
                            sortable
                          />
                          <Column
                            field="status"
                            header="Status"
                            body={(rowData) => {
                              const normalizedStatus = normalizeReservationStatus(rowData.status);
                              const isCheckedOut = normalizedStatus === 'checked-out';
                              return (
                                <span className={`status-badge ${isCheckedOut ? 'bg-warning' : 'bg-info'}`}>
                                  {isCheckedOut ? 'Checked Out (Balance Due)' : 'Completed'}
                                </span>
                              );
                            }}
                            sortable
                          />
                          <Column
                            header="Actions"
                            body={(rowData) => {
                              const isCheckedOut = normalizeReservationStatus(rowData.status) === 'checked-out';
                              return (
                                <div className="d-flex gap-2">
                                  <Button
                                    icon="ti ti-eye"
                                    className="p-button-rounded p-button-info p-button-sm"
                                    onClick={() => openDetailsModal(rowData)}
                                    tooltip="View Details"
                                    tooltipOptions={{ position: 'top' }}
                                  />
                                  {isCheckedOut && (
                                    <Button
                                      icon="ti ti-credit-card"
                                      className="p-button-rounded p-button-warning p-button-sm"
                                      onClick={() => openRecordPaymentDialog(rowData)}
                                      tooltip="Record Payment"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                </div>
                              );
                            }}
                            style={{ width: '160px', textAlign: 'center' }}
                          />
                        </DataTable>
                      </div>
                    </TabPanel>
                  </TabView>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Listing Dialog */}
      {showListingDialog && selectedUnit && (
        <ListUnitForBookingDialog
          visible={showListingDialog}
          onHide={() => {
            setShowListingDialog(false);
            setSelectedUnit(null);
          }}
          unit={selectedUnit}
          facilityId={facilityId}
          currencies={currencies}
          onSuccess={handleListingSuccess}
        />
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedUnitForBooking && (
        <BookingModal
          visible={showBookingModal}
          onHide={() => setShowBookingModal(false)}
          unit={selectedUnitForBooking}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && selectedReservation && (
        <CheckoutModal
          visible={showCheckoutModal}
          onHide={() => setShowCheckoutModal(false)}
          reservation={selectedReservation}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      <BookingInvoiceDialog
        visible={showBookingInvoiceDialog}
        onHide={() => {
          setShowBookingInvoiceDialog(false);
          setSelectedBookingInvoiceId(null);
        }}
        invoiceId={selectedBookingInvoiceId}
        onCheckInSuccess={async () => {
          const fid = await getItem("selectedFacilityId");
          if (fid) await fetchReservations(fid);
        }}
        onPaymentSuccess={async () => {
          const fid = await getItem("selectedFacilityId");
          if (fid) await fetchReservations(fid);
        }}
      />

      {showRecordPaymentDialog && selectedInvoice && (
        <RecordPaymentDialog
          visible={showRecordPaymentDialog}
          onHide={() => setShowRecordPaymentDialog(false)}
          invoice={selectedInvoice}
          onPaymentSuccess={async () => {
            setShowRecordPaymentDialog(false);
            try {
              await fetchReservations(facilityId);
            } catch (error) {
              console.error("Error refreshing reservations after payment:", error);
            }
          }}
        />
      )}

      {/* Edit Dialog */}
      {showEditDialog && selectedUnit && (
        <Dialog
          header="Edit Booking Property"
          visible={showEditDialog}
          style={{ width: '80%' }}
          onHide={() => setShowEditDialog(false)}
          modal
          className="p-fluid"
        >
          <EditBookingPropertyForm
            visible={showEditDialog}
            onHide={() => setShowEditDialog(false)}
            unit={selectedUnit}
            currencies={currencies}
            facilityId={facilityId}
            onSuccess={async () => {
              toastify("Property updated successfully", "success");
              setShowEditDialog(false);
              await fetchAllUnitsAndProperties(facilityId);
            }}
          />
        </Dialog>
      )}

      {/* Block Dates Dialog */}
      {showBlockingDialog && selectedUnit && (
        <BlockDatesComponent
          visible={showBlockingDialog}
          onHide={() => setShowBlockingDialog(false)}
          facilityId={facilityId}
          propertyData={{
            id: selectedUnit.bookingProperty?.id || selectedUnit.bookingProperty?._id,
            _id: selectedUnit.bookingProperty?.id || selectedUnit.bookingProperty?._id,
            unit: {
              id: selectedUnit._id || selectedUnit.id,
              _id: selectedUnit._id || selectedUnit.id,
              name: selectedUnit.name
            }
          }}
          onSuccess={handleBlockingSuccess}
        />
      )}

      {showDetailsModal && selectedReservationId && (
        <ReservationDetailsModal
          visible={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          reservationId={selectedReservationId}
          facilityId={facilityId}
        />
      )}

      {/* Cancel Reservation Dialog */}
      {showCancelDialog && reservationToCancel && (
        <Dialog
          header="Cancel Reservation"
          visible={showCancelDialog}
          onHide={closeCancelDialog}
          style={{ width: '450px' }}
          modal
          className="p-fluid"
          footer={
            <div className="d-flex justify-content-end gap-2">
              <Button
                label="Close"
                className="p-button-text"
                onClick={closeCancelDialog}
                disabled={cancelSubmitting}
              />
              <Button
                label={cancelSubmitting ? "Cancelling..." : "Confirm Cancel"}
                icon="ti ti-x"
                className="p-button-danger"
                onClick={handleCancelReservation}
                loading={cancelSubmitting}
              />
            </div>
          }
        >
          <div className="mb-3">
            <label className="form-label">Cancellation Reason</label>
            <InputTextarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Provide a reason for cancellation..."
              className="w-100"
            />
          </div>
          <div className="mb-2">
            <label className="form-label">
              Refund Amount (optional) {getReservationCurrencyCode(reservationToCancel) !== 'N/A' && (
                <span className="text-muted">({getReservationCurrencyCode(reservationToCancel)})</span>
              )}
            </label>
            <InputNumber
              value={cancelRefundAmount}
              onValueChange={(e) => setCancelRefundAmount(e.value || 0)}
              min={0}
              mode="decimal"
              className="w-100"
              placeholder="0.00"
              prefix={getReservationCurrencyCode(reservationToCancel) !== 'N/A' ? `${getReservationCurrencyCode(reservationToCancel)} ` : ''}
            />
          </div>
          <small className="text-muted">
            Cancelling will release the unit back to available status for the selected dates.
          </small>
        </Dialog>
      )}
    </Layout>
  );
};

export default UnitListing;
